import type { ComponentType } from "react";
import { Clock3, Phone, PhoneIncoming, Voicemail } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn, formatPercentage } from "@/lib/utils";
import { useAgentPerformance } from "@/hooks/useAgentPerformance";
import { useAgentPerQueueBreakdown } from "@/hooks/useAgentPerQueueBreakdown";
import { useAgentModalKpiWidgets } from "@/hooks/useAgentModalKpiWidgets";
import { formatDuration } from "./tableCellHelpers";
import { UserAgentPerQueueBreakdownTable } from "./UserAgentPerQueueBreakdownTable";
import { getPerformanceHeaderRangeLabel } from "@/utils/dateRangeUtils";
import { CallMissedIcon } from "@/components/icons/CallMissedIcon";
import { ClockCheck } from "@/components/icons/ClockCheck";
import { WidgetTitle } from "@/components/widgets/WidgetTitle";

function slaClass(v: number) {
  if (v >= 80) return "text-success dark:text-success";
  if (v >= 70) return "text-caution";
  return "text-destructive";
}

function KpiCard({
  label, value, value2, sub, colorClass, valueClassName, tooltip, icon: Icon,
}: { readonly label: string; readonly value: string; readonly value2?: string; readonly sub?: string; readonly colorClass?: string; readonly valueClassName?: string; readonly tooltip?: string; readonly icon: ComponentType<{ className?: string }> }) {
  return (
    <div className="flex min-h-[72px] w-full min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-card p-4 text-card-foreground dark:border-white/15">
      <div className="flex min-w-0 flex-row items-center justify-between gap-2">
        <WidgetTitle className="truncate">{label}</WidgetTitle>
        {tooltip ? (
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <span className="cursor-pointer shrink-0">
                  <Icon className="h-4 w-4 text-primary dark:text-white" aria-hidden />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs font-normal">
                <p>{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Icon className="h-4 w-4 text-primary dark:text-white shrink-0" aria-hidden />
        )}
      </div>
      <div className={cn(valueClassName ?? "text-2xl font-bold text-primary dark:text-white", "my-1.5", colorClass)}>
        {value}
        {value2 != null && <span className="text-muted-foreground dark:text-white font-semibold"> / {value2}</span>}
      </div>
      {sub && <div className="text-xs text-muted-foreground dark:text-white/70">{sub}</div>}
    </div>
  );
}

interface Props {
  readonly agentId: string | null;
  readonly startDate: string | undefined;
  readonly endDate: string | undefined;
  /** TCX-6500: Manager's role-scoped queue ids (or the EMPTY_QUEUE_ID sentinel); null = unrestricted. */
  readonly queueMicrosoftIds?: string[] | null;
}

export function UserAgentOverviewTab({ agentId, startDate, endDate, queueMicrosoftIds }: Props) {
  const { performanceData } = useAgentPerformance(agentId, startDate, endDate, queueMicrosoftIds);
  const { totalHandled, totalMissed } = useAgentPerQueueBreakdown(agentId, startDate, endDate, queueMicrosoftIds);
  const { voicemail, internal } = useAgentModalKpiWidgets(agentId, startDate, endDate, queueMicrosoftIds);
  const rangeLabel = startDate && endDate ? getPerformanceHeaderRangeLabel(startDate, endDate) : "";

  return (
    <div className="flex flex-col gap-3">
      <section>
        <h3 className="mb-2 text-[12px] font-bold uppercase tracking-[.09em] text-muted-foreground dark:text-slate-300">
          Performance{rangeLabel && <span className="tracking-normal"> · {rangeLabel}</span>}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
          <KpiCard label="Calls handled" value={String(totalHandled)} icon={PhoneIncoming} tooltip="Total call handled by the user within given day" />
          <KpiCard label="Calls missed" value={String(totalMissed)} colorClass="text-call-status-missed" icon={CallMissedIcon} tooltip="Total call have not been picked up within given day" />
          <KpiCard
            label="SLA"
            value={performanceData?.slaPercent == null ? "No information" : formatPercentage(performanceData.slaPercent)}
            colorClass={performanceData?.slaPercent == null ? undefined : slaClass(performanceData.slaPercent)}
            valueClassName={performanceData?.slaPercent == null ? "text-xs font-normal text-muted-foreground dark:text-white/70" : undefined}
            icon={ClockCheck}
            tooltip="The percentage of total incoming calls handled within 60 seconds within given day"
          />
          <KpiCard label="AHT" value={formatDuration(performanceData?.ahtSeconds ?? 0)} icon={Clock3} tooltip="The average amount of time spent on each handled call within given day" />
          <KpiCard label="Voicemail" value={String(voicemail)} icon={Voicemail} tooltip="The call sent to voicemail, within given day" />
          <KpiCard label="Internal" value={String(internal)} icon={Phone} tooltip="The calls among agents within given day" />
        </div>
      </section>

      <section>
        <UserAgentPerQueueBreakdownTable agentId={agentId} startDate={startDate} endDate={endDate} queueMicrosoftIds={queueMicrosoftIds} />
      </section>
    </div>
  );
}
